import { useId, useState } from "react";
import { useTranslation } from "react-i18next";

import { useDeleteScreenshot } from "@/api/queries";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { Label } from "@/components/ui/input";
import { errorText } from "@/lib/errors";

type Props = {
  screenshotId: string;
  /** "10:00–10:10" */
  range: string;
  onClose: () => void;
  onDeleted: () => void;
};

/** Asks an administrator for the reason before deleting a screenshot with its slot time. */
export function DeleteScreenshotDialog({ screenshotId, range, onClose, onDeleted }: Props) {
  const { t } = useTranslation();
  const id = useId();
  const [reason, setReason] = useState("");
  const del = useDeleteScreenshot();
  const valid = reason.trim().length >= 3;

  return (
    <ConfirmDialog
      title={t("timeline.delete.title")}
      description={t("timeline.delete.description", { range })}
      confirmLabel={t("timeline.delete.confirm")}
      danger
      confirmDisabled={!valid}
      errorText={(err) => errorText(t, err)}
      onConfirm={async () => {
        await del.mutateAsync({ screenshotId, reason: reason.trim() });
        onDeleted();
      }}
      onClose={onClose}
    >
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={id}>{t("timeline.delete.reason")}</Label>
        <textarea
          id={id}
          value={reason}
          maxLength={500}
          rows={3}
          autoFocus
          onChange={(e) => setReason(e.target.value)}
          className="w-full rounded-lg border bg-surface px-3 py-2 shadow-card"
          aria-describedby={`${id}-hint`}
        />
        <span id={`${id}-hint`} className="text-xs text-muted">
          {t("timeline.delete.reasonHint")}
        </span>
      </div>
    </ConfirmDialog>
  );
}
